<template>
  <div class="split-equipment-view">
    <div class="split-header">
      <h3>Dual Stack Equipment Selection</h3>
      <p>Select equipment for Upper and Lower stacks</p>
      <div class="header-actions">
        <UseExistingEquipmentButton />
      </div>
    </div>
    <div class="split-container">
      <!-- Upper Stack (Left) - Blue -->
      <div class="stack-panel upper-panel">
        <div class="stack-label upper-label">Upper Stack</div>
        <div ref="upperContent" class="stack-content">
          <Intakes v-if="isEspOnShore" stack="upper" />
          <Pumps v-if="!isEspOnShore" stack="upper" />
          <Pumps v-if="isEspOnShore" stack="upper" />
          <SealSensorMle v-if="!isEspOnShore" stack="upper" />
          <Motor stack="upper" />
          <SealSensorMle v-if="isEspOnShore" stack="upper" />
          <Cables v-if="isEspOnShore" stack="upper" />
        </div>
      </div>

      <!-- Lower Stack (Right) - Green -->
      <div class="stack-panel lower-panel">
        <div class="stack-label lower-label">Lower Stack</div>
        <div ref="lowerContent" class="stack-content">
          <Intakes v-if="isEspOnShore" stack="lower" />
          <Pumps v-if="!isEspOnShore" stack="lower" />
          <Pumps v-if="isEspOnShore" stack="lower" />
          <SealSensorMle v-if="!isEspOnShore" stack="lower" />
          <Motor stack="lower" />
          <SealSensorMle v-if="isEspOnShore" stack="lower" />
          <Cables v-if="isEspOnShore" stack="lower" />
        </div>
      </div>
    </div>
    <!-- Surface Drive - Outside split, single instance -->
    <SurfaceDrive />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted, watch } from "vue";
import Intakes from "@/views/scenarios/sizingParameters/Intakes.vue";
import Pumps from "@/views/scenarios/sizingParameters/Pumps.vue";
import Motor from "@/views/scenarios/sizingParameters/Motor.vue";
import SealSensorMle from "@/views/scenarios/sizingParameters/SealSensorMle.vue";
import Cables from "@/views/scenarios/sizingParameters/Cables.vue";
import SurfaceDrive from "@/views/scenarios/sizingParameters/SurfaceDrive.vue";
import UseExistingEquipmentButton from "@/components/scenarios/UseExistingEquipmentButton.vue";
import { sizingFormKey } from "@/composables/scenarioDetailProvider/scenarioDetailProviderKeys";
import { injectStrict } from "@/utils/utils";
import { Sizing_Pumping_Type_Enum } from "gql/api";

const sizingForm = injectStrict(sizingFormKey, null);

const isEspOnShore = computed(() => {
  return (
    sizingForm?.data.value.pumping_type === Sizing_Pumping_Type_Enum.EspOnShore
  );
});

// Alignment: match heights of corresponding sections, allowing shrink
const upperContent = ref<HTMLElement | null>(null);
const lowerContent = ref<HTMLElement | null>(null);

const alignSections = () => {
  if (!upperContent.value || !lowerContent.value) return;

  const upperSections = Array.from(
    upperContent.value.children
  ) as HTMLElement[];
  const lowerSections = Array.from(
    lowerContent.value.children
  ) as HTMLElement[];

  // First, reset ALL min-heights to allow natural sizing
  upperSections.forEach(el => {
    el.style.minHeight = "";
  });
  lowerSections.forEach(el => {
    el.style.minHeight = "";
  });

  // Force a reflow by reading layout properties
  // Accessing getBoundingClientRect forces the browser to recalculate layout
  if (upperContent.value) {
    upperContent.value.getBoundingClientRect();
  }
  if (lowerContent.value) {
    lowerContent.value.getBoundingClientRect();
  }

  // Now match heights of corresponding sections
  // Only align if there's a meaningful difference (threshold: 5px)
  const ALIGNMENT_THRESHOLD = 5;

  upperSections.forEach((upperEl, idx) => {
    const lowerEl = lowerSections[idx];
    if (!lowerEl) return;

    // Get natural heights after reset and reflow
    const upperHeight = upperEl.offsetHeight;
    const lowerHeight = lowerEl.offsetHeight;
    const heightDiff = Math.abs(upperHeight - lowerHeight);

    // Only align if difference exceeds threshold to avoid creating gaps
    if (heightDiff > ALIGNMENT_THRESHOLD) {
      const maxHeight = Math.max(upperHeight, lowerHeight);
      upperEl.style.minHeight = `${maxHeight}px`;
      lowerEl.style.minHeight = `${maxHeight}px`;
    } else {
      // Heights are close enough, remove minHeight to allow natural sizing
      upperEl.style.minHeight = "";
      lowerEl.style.minHeight = "";
    }
  });
};

// Use ResizeObserver to watch both containers and individual sections
let resizeObserver: ResizeObserver | null = null;
let alignTimeout: ReturnType<typeof setTimeout> | null = null;

const scheduleAlign = () => {
  if (alignTimeout) clearTimeout(alignTimeout);
  alignTimeout = setTimeout(() => {
    alignSections();
  }, 10); // Small delay to batch updates
};

onMounted(() => {
  // Initial alignment after DOM is ready
  setTimeout(() => {
    alignSections();
  }, 100);

  resizeObserver = new ResizeObserver(() => {
    scheduleAlign();
  });

  // Observe the containers
  if (upperContent.value) resizeObserver.observe(upperContent.value);
  if (lowerContent.value) resizeObserver.observe(lowerContent.value);

  // Also observe individual sections for more responsive updates
  if (upperContent.value) {
    Array.from(upperContent.value.children).forEach(child => {
      resizeObserver?.observe(child as HTMLElement);
    });
  }
  if (lowerContent.value) {
    Array.from(lowerContent.value.children).forEach(child => {
      resizeObserver?.observe(child as HTMLElement);
    });
  }
});

onUnmounted(() => {
  if (resizeObserver) resizeObserver.disconnect();
  if (alignTimeout) clearTimeout(alignTimeout);
});

watch(isEspOnShore, () => {
  // Wait for DOM to update after conditional rendering
  setTimeout(() => {
    alignSections();
    // Re-observe new sections after conditional render
    if (resizeObserver && upperContent.value) {
      Array.from(upperContent.value.children).forEach(child => {
        resizeObserver?.observe(child as HTMLElement);
      });
    }
    if (resizeObserver && lowerContent.value) {
      Array.from(lowerContent.value.children).forEach(child => {
        resizeObserver?.observe(child as HTMLElement);
      });
    }
  }, 50);
});
</script>

<style lang="scss" scoped>
.split-equipment-view {
  width: 100%;
  display: block;
  visibility: visible;
  opacity: 1;
  padding: 1rem;
}

.split-header {
  text-align: center;
  margin-bottom: 1.5rem;
  padding: 0.75rem;
  background-color: var(--primary-50);
  border-radius: 8px;

  h3 {
    margin: 0 0 0.4rem 0;
    color: var(--primary-color);
    font-size: 1.4rem;
  }

  p {
    margin: 0;
    color: var(--gray-600);
    font-size: 0.95rem;
  }

  .header-actions {
    margin-top: 1rem;
    display: flex;
    justify-content: center;
  }
}

.split-container {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  width: 100%;
  padding: 0.75rem;
  box-sizing: border-box;
}

.stack-panel {
  display: flex;
  flex-direction: column;
  min-width: 0; // Prevents overflow
  background-color: var(--surface-ground);
  border-radius: 8px;
  padding: 1rem;
  min-height: 500px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  overflow-y: auto;
  overflow-x: hidden; // Prevent horizontal scroll
}

// Lower Stack - Green outline
.lower-panel {
  border: 3px solid #10b981; // Green-500
  background-color: rgba(16, 185, 129, 0.02); // Very light green tint
}

// Upper Stack - Blue outline
.upper-panel {
  border: 3px solid #3b82f6; // Blue-500
  background-color: rgba(59, 130, 246, 0.02); // Very light blue tint
}

.stack-label {
  font-weight: 600;
  font-size: 1.1rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 1rem;
  padding: 0.75rem;
  padding-bottom: 0.6rem;
  border-bottom: 2px solid var(--border-main);
  background-color: var(--gray-50);
  border-radius: 4px 4px 0 0;
}

.lower-label {
  color: #059669; // Green-600
  background-color: rgba(16, 185, 129, 0.1); // Light green background
}

.upper-label {
  color: #2563eb; // Blue-600
  background-color: rgba(59, 130, 246, 0.1); // Light blue background
}

.stack-content {
  display: flex;
  flex-direction: column;
  gap: 0;
  width: 100%;
  min-width: 0; // Prevent overflow
  overflow-x: hidden; // Prevent horizontal scroll
}

.stack-content > * {
  width: 100%;
  min-width: 0; // Prevent child elements from causing overflow
  overflow-x: hidden; // Prevent horizontal scroll in children
  // Ensure smooth height transitions
  transition: min-height 0.15s ease-out;
  // Allow content to shrink naturally
  box-sizing: border-box;
}

@media only screen and (max-width: 1200px) {
  .split-container {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
}
</style>
